import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { CircularProgress, Box } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { setUser } from '../store/authSlice';
import { auth } from '../config/firebase';
import { peekReturnTo } from '../utils/returnTo';

interface UnprotectedRouteProps {
  children: React.ReactNode;
}

const UnprotectedRoute: React.FC<UnprotectedRouteProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      dispatch(setUser(user));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [dispatch]);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        className="bg-surface-subtle"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!isAuthenticated) return <>{children}</>;

  // An already-authenticated visit to /login still honours a deep link the
  // user was bounced from — peek rather than consume, since the login page's
  // own post-auth effect resolves the same destination and the two must
  // agree instead of racing. ProtectedRoute clears it on arrival.
  return <Navigate to={peekReturnTo() || '/dashboard'} replace />;
};

export default UnprotectedRoute;